/**
 * Rebuy Smart Cart — NSS bundle support.
 *
 * Cart lines added by nss-buybox-seeds.js share a `_is_bundle_group` property
 * (`n-<uid>`). This script:
 * 1. Attaches `nss*` helper methods to the Smart Cart Vue instance so the
 *    custom Smart Cart template (Rebuy admin) can group those lines into one
 *    bundle box with a single quantity stepper. Each line scales by its own
 *    `_bundle_qty` unit (e.g. 1×16oz vs 2×8oz per bundle).
 * 2. Keeps the sprouter's `_is_bundle` property in sync with the seed's
 *    subscription state: `n-sprouters-<uid>` while the seed has a selling
 *    plan, plain `n-<uid>` when the seed is one-time.
 */
(() => {
  const GROUP_PROP = `_is_bundle_group`;
  const BUNDLE_PROP = `_is_bundle`;
  const UNIT_PROP = `_bundle_qty`;
  const TITLE_PROP = `_bundle_title`;
  const DEFAULT_TITLE = `The Starter Kit`;

  let smartCart = null;
  let reconciling = false;

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || `/`;

  /**
   * Cart mode (drawer vs cart page A/B test). `window.NSS_CART_MODE` is set
   * inline in layout/theme.liquid from the theme setting, a `nss_cart_mode`
   * cookie or a `?cart_mode=` param, so an A/B tool can flip it per visitor.
   * In `page` mode every "open the cart" path navigates to /cart instead of
   * showing the Smart Cart, and the header cart link is left alone by Rebuy.
   */
  const isPageMode = () => window.NSS_CART_MODE === `page`;
  const cartPageUrl = () => routeRoot().replace(/\/$/, ``) + `/cart`;
  const isCartPage = () => /\/cart\/?$/.test(window.location.pathname);
  const hasThemeCartTemplate = () => !!document.querySelector(`nss-cart-template`);
  const goToCartPage = () => {
    if (!isCartPage()) window.location.href = cartPageUrl();
  };

  const lineProps = (item) => (item && item.properties) || {};
  const groupKey = (item) => lineProps(item)[GROUP_PROP] || ``;
  const unitQty = (item) => {
    const n = parseInt(lineProps(item)[UNIT_PROP], 10);
    return n > 0 ? n : 1;
  };
  const hasSellingPlan = (item) =>
    !!(item && (item.selling_plan_allocation || item.selling_plan));

  // Smart Cart monkey-patches window.fetch and auto-refreshes (items,
  // subtotal, discounts) whenever a /cart/*.js endpoint is called — so cart
  // mutations need no manual sync. Never feed raw cart.js JSON into
  // SmartCart.setCart(): it replaces Rebuy's enriched cart state and freezes
  // subtotal/price updates afterwards.
  const cartRequest = (endpoint, payload) =>
    fetch(routeRoot() + endpoint, {
      method: `POST`,
      headers: { "Content-Type": `application/json` },
      body: JSON.stringify(payload),
    }).then((res) => {
      if (!res.ok) throw new Error(`${endpoint} failed (${res.status})`);
      return res.json();
    });

  const methods = {
    nssIsBundleItem(item) {
      return !!groupKey(item);
    },

    // Seed = the subscription-capable line; the sprouter has no selling plans.
    nssIsSeedItem(item) {
      if (hasSellingPlan(item)) return true;
      if (typeof this.hasSwitchToSubscription === `function`) {
        return !!this.hasSwitchToSubscription(item);
      }
      const product = (item && item.product) || {};
      return !!(
        product.subscription ||
        (product.subscription_frequencies || []).length
      );
    },

    nssBundleUnitQty(item) {
      return unitQty(item);
    },

    // PRODUCT metafield custom.cart_short_description, from Rebuy's enriched
    // item data (must be enabled for cart items in the Rebuy admin). Rebuy
    // enriches product-level metafields only — variant metafields are not
    // exposed (verified live 2026-08-13). Handles both shapes: nested
    // {namespace:{key}} objects and arrays of {namespace,key,value}.
    nssShortDescription(item) {
      const read = (metafields, namespace, key) => {
        if (!metafields) return ``;
        if (Array.isArray(metafields)) {
          const hit = metafields.find((m) => m && m.namespace === namespace && m.key === key);
          return (hit && hit.value) || ``;
        }
        const ns = metafields[namespace];
        if (!ns) return ``;
        const value = ns[key];
        if (value == null) return ``;
        return typeof value === `object` ? value.value || `` : value;
      };
      return read(item.product && item.product.metafields, `custom`, `cart_short_description`) || ``;
    },

    // Groups cart lines by `_is_bundle_group`, in cart order.
    // count = bundles in cart, derived from line quantity / unit quantity.
    nssBundleGroups() {
      const groups = new Map();
      (this.items() || []).forEach((item) => {
        const key = groupKey(item);
        if (!key) return;
        if (typeof this.isHiddenItem === `function` && this.isHiddenItem(item)) return;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            title: ``,
            items: [],
            mainItems: [],
            seedItems: [],
            count: 1,
          });
        }
        const group = groups.get(key);
        group.items.push(item);
        if (this.nssIsSeedItem(item)) group.seedItems.push(item);
        else group.mainItems.push(item);
        if (!group.title) group.title = lineProps(item)[TITLE_PROP] || ``;
      });
      groups.forEach((group) => {
        if (!group.title) group.title = DEFAULT_TITLE;
        const counts = group.items.map((item) =>
          Math.max(1, Math.floor(item.quantity / unitQty(item)))
        );
        group.count = counts.length ? Math.min.apply(null, counts) : 1;
      });
      return Array.from(groups.values());
    },

    // "2 x 8oz bags" — total bags in cart × bag size (variant title).
    nssSeedQtyLabel(item) {
      const bags = item.quantity;
      const size = item.variant_title ? `${item.variant_title} ` : ``;
      return `${bags} x ${size}bag${bags > 1 ? `s` : ``}`;
    },

    nssDiscountBadge(item) {
      // Subscription lines: the discount lives on the selling plan, not the
      // variant compare-at — compare the plan price against the one-time price.
      const allocation = item && item.selling_plan_allocation;
      if (allocation && allocation.compare_at_price && allocation.price != null) {
        const planPrice = Number(allocation.price);
        const planCompare = Number(allocation.compare_at_price);
        if (planCompare > planPrice) {
          const planPercent = Math.round((1 - planPrice / planCompare) * 100);
          if (planPercent > 0) return `${planPercent}% Off`;
        }
      }
      if (typeof this.itemHasDiscount === `function` && !this.itemHasDiscount(item)) {
        return ``;
      }
      // itemPrice() returns integer cents but compareAtPrice() returns a
      // dollar string ("199.98") — normalize both to cents before comparing.
      const toCents = (value) => {
        const num = Number(value);
        if (!num || Number.isNaN(num)) return 0;
        return String(value).includes(`.`) ? Math.round(num * 100) : num;
      };
      const price = toCents(this.itemPrice(item));
      const compare = toCents(this.compareAtPrice(item));
      if (!compare || !price || compare <= price) return ``;
      const percent = Math.round((1 - price / compare) * 100);
      return percent > 0 ? `${percent}% Off` : ``;
    },

    // "You save $XX" badge for the cart subtotal. Rebuy renders the current
    // amount + compare-at amount inside subtotal()'s v-html output, so we parse
    // that same HTML (guarantees the number matches what's on screen) and
    // return the difference. Empty string when there's no compare-at (no
    // discount) so the template's v-if hides the badge. Method bodies are plain
    // JS — globals like document/Number are fine here; only TEMPLATE
    // expressions must route through _self (see GOTCHA 1).
    nssSubtotalSavings() {
      if (typeof this.subtotal !== `function`) return ``;
      const box = document.createElement(`div`);
      box.innerHTML = this.subtotal() || ``;
      const compareEl = box.querySelector(
        `.rebuy-cart__flyout-subtotal-compare-amount`
      );
      if (!compareEl) return ``;
      const compareText = compareEl.textContent || ``;
      const toCents = (str) => {
        const cleaned = String(str).replace(/[^0-9.,]/g, ``).replace(/,/g, ``);
        const num = parseFloat(cleaned);
        return Number.isNaN(num) ? null : Math.round(num * 100);
      };
      const compareCents = toCents(compareText);
      compareEl.remove();
      const finalCents = toCents(box.textContent || ``);
      if (compareCents == null || finalCents == null || compareCents <= finalCents) {
        return ``;
      }
      // Rebuy prefixes the compare amount with an "Original" sr-only label, so
      // grab the currency symbol (a non-word char) sitting right before the
      // digits — not the leading text.
      const symbolMatch = compareText.match(/([^\w\s])\s*[\d.,]+/);
      const symbol = symbolMatch ? symbolMatch[1] : `$`;
      const saved = ((compareCents - finalCents) / 100).toFixed(2);
      return `You save ${symbol}${saved}`;
    },

    // Sets the bundle count; every line becomes count × its unit quantity.
    // One atomic cart/update.js call so lines can't drift out of ratio.
    nssSetBundleQty(group, count) {
      const next = Math.max(0, parseInt(count, 10) || 0);
      // Same spinner behavior as Rebuy's own steppers: the template swaps the
      // icon while status is set; Rebuy's refresh rebuilds items and clears it.
      const status = next < group.count ? `decreasing` : `increasing`;
      const updates = {};
      group.items.forEach((item) => {
        item.status = status;
        updates[item.key] = next === 0 ? 0 : next * unitQty(item);
      });
      return cartRequest(`cart/update.js`, { updates }).catch((err) => {
        group.items.forEach((item) => {
          item.status = ``;
        });
        console.error(`[NSS][Rebuy Bundle]`, err);
      });
    },

    nssRemoveBundle(group) {
      return this.nssSetBundleQty(group, 0);
    },
  };

  // The Smart Cart Vue instance renders the custom template; its root
  // element is #rebuy-cart, so Vue 2 exposes it there as __vue__.
  const findVm = () => {
    const el = document.querySelector(`#rebuy-cart`);
    return (el && el.__vue__) || null;
  };

  const routeShowToCartPage = (sc) => {
    if (!sc || sc.__nssShowRouted || typeof sc.show !== `function`) return;
    const originalShow = sc.show;
    sc.show = function () {
      if (isPageMode()) return goToCartPage();
      return originalShow.apply(this, arguments);
    };
    sc.__nssShowRouted = true;
  };

  const attach = (instance) => {
    if (!instance) return;
    if (!smartCart) smartCart = instance;
    if (instance === (window.Rebuy && window.Rebuy.SmartCart)) routeShowToCartPage(instance);
    if (instance.__nssBundlesAttached) return;
    Object.keys(methods).forEach((name) => {
      instance[name] = methods[name].bind(instance);
    });
    instance.__nssBundlesAttached = true;
    if (typeof instance.$forceUpdate === `function`) instance.$forceUpdate();
  };

  const attachAll = (eventInstance) => {
    attach(eventInstance);
    attach(findVm());
    if (window.Rebuy && window.Rebuy.SmartCart) attach(window.Rebuy.SmartCart);
  };

  /**
   * Keeps bundle line properties consistent after cart mutations:
   * - Orphaned group (a bundle product was removed, one line left): the
   *   remaining line becomes a REGULAR product — every bundle property is
   *   blanked. Values are set to `""` rather than dropped because a non-empty
   *   `properties` object reliably replaces the set on cart/change.js, while
   *   an empty one may be ignored; empty values behave as untagged everywhere
   *   (no grouping, no `n-sprouters-` discount match, hidden `_` props).
   * - Re-tags after subscription switches (see nss-buybox-seeds.js addToCart
   *   for the rules): a group where some line has a selling plan → plan-less
   *   lines (the sprouter) get `_is_bundle: n-sprouters-<uid>`; a group with
   *   no selling plan → every line gets plain `n-<uid>`.
   * Idempotent: only issues cart/change.js calls when something differs, so
   * the refresh it triggers converges instead of looping.
   */
  const BUNDLE_KEYS = [BUNDLE_PROP, GROUP_PROP, UNIT_PROP, TITLE_PROP];
  // Resolves to true when it changed any line, false otherwise.
  const reconcileBundleTags = () => {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return fetch(routeRoot() + `cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        const groups = {};
        (cart.items || []).forEach((line) => {
          const key = (line.properties || {})[GROUP_PROP];
          if (!key) return;
          (groups[key] = groups[key] || []).push(line);
        });

        const changes = [];
        Object.keys(groups).forEach((key) => {
          const lines = groups[key];

          // Bundle lost a member — strip the survivor back to a regular product.
          if (lines.length < 2) {
            lines.forEach((line) => {
              const props = line.properties || {};
              if (!BUNDLE_KEYS.some((k) => props[k])) return;
              const newProps = { ...props };
              BUNDLE_KEYS.forEach((k) => {
                if (k in newProps) newProps[k] = ``;
              });
              changes.push({ id: line.key, quantity: line.quantity, properties: newProps });
            });
            return;
          }

          const groupHasPlan = lines.some((line) => line.selling_plan_allocation);
          const uid = key.replace(/^n-/, ``);
          lines.forEach((line) => {
            const expected =
              groupHasPlan && !line.selling_plan_allocation
                ? `n-sprouters-${uid}`
                : key;
            if ((line.properties || {})[BUNDLE_PROP] !== expected) {
              changes.push({
                id: line.key,
                quantity: line.quantity,
                properties: { ...line.properties, [BUNDLE_PROP]: expected },
              });
            }
          });
        });

        if (!changes.length) return false;
        // Sequential cart/change.js calls; Rebuy picks up the changes itself.
        return changes
          .reduce(
            (chain, change) => chain.then(() => cartRequest(`cart/change.js`, change)),
            Promise.resolve()
          )
          .then(() => true);
      })
      .catch((err) => {
        console.error(`[NSS][Rebuy Bundle] reconcile`, err);
        return false;
      })
      .finally(() => {
        reconciling = false;
      });
  };
  // The cart page (nss-cart-template.js) runs without the Smart Cart, so it
  // calls this itself after its own cart mutations.
  window.NSS_RECONCILE_BUNDLE_TAGS = reconcileBundleTags;

  document.addEventListener(`rebuy:smartcart.init`, (e) => {
    attachAll(e.detail && e.detail.smartcart);
  });
  document.addEventListener(`rebuy:smartcart.ready`, (e) => {
    attachAll(e.detail && e.detail.smartcart);
    reconcileBundleTags();
  });
  document.addEventListener(`rebuy:smartcart.item-switch-to-subscription`, () => {
    reconcileBundleTags();
  });
  document.addEventListener(`rebuy:smartcart.item-switch-to-one-time`, () => {
    reconcileBundleTags();
  });
  document.addEventListener(`rebuy:smartcart.line-item-removed`, () => {
    reconcileBundleTags();
  });
  // Safety net for mutations that don't fire the events above (e.g. older
  // Smart Cart builds); reconcile is diff-based so extra runs are no-ops.
  document.addEventListener(`rebuy:cart.change`, () => {
    reconcileBundleTags();
  });

  /**
   * Theme ATC flows (nss-buybox-seeds.js, nss-buybox.js, nss-quick-add.js)
   * call NSS_REFRESH_AND_OPEN_CART after cart/add.js; it used to be defined
   * by nss-cart-template.js, whose section was removed with the old drawer.
   * Rebuy already refreshes its data from the add fetch, so opening is all
   * that's left to do. Retries briefly in case Rebuy is still booting.
   */
  const openSmartCart = (tries) => {
    if (isPageMode()) return goToCartPage();
    const remaining = typeof tries === `number` ? tries : 20;
    const sc = window.Rebuy && window.Rebuy.SmartCart;
    if (sc && typeof sc.show === `function`) return sc.show();
    const vm = findVm();
    if (vm && typeof vm.show === `function`) return vm.show();
    if (remaining > 0) setTimeout(() => openSmartCart(remaining - 1), 250);
  };
  window.NSS_REFRESH_AND_OPEN_CART = () => openSmartCart();
  window.NSS_OPEN_CART_DRAWER = () => openSmartCart();

  /**
   * Ported from nss-cart-template.js (removed with the old drawer) so regular
   * products keep working with the Smart Cart:
   * - NSS_BUILD_ADD_ITEMS: main item + active nss-buybox-addon from a product
   *   form (used by nss-buybox.js and the submit interceptor below).
   * - NSS_DIRECT_CHECKOUT: clear cart, add form items, go straight to checkout.
   * - A document-level interceptor for `.shopify-product-form` submits and
   *   name="add" clicks, which the old script handled globally.
   */
  window.NSS_BUILD_ADD_ITEMS = (form, opts = {}) => {
    const scope = form.closest(`[id^="shopify-section"]`) || document;
    const addon = scope.querySelector(`nss-buybox-addon[data-active="true"]`);
    if (!addon) return null;

    const fd = new FormData(form);
    const mainPlan = opts.otp ? `` : fd.get(`selling_plan`) || ``;
    const main = { id: fd.get(`id`), quantity: parseInt(fd.get(`quantity`)) || 1 };
    if (mainPlan) main.selling_plan = mainPlan;

    const addonId = addon.getAttribute(`data-addon-variant-id`);
    if (!addonId) return [main];

    const addonPlanId = addon.getAttribute(`data-addon-plan-id`) || ``;
    const mode = addon.getAttribute(`data-addon-plan-mode`) || `follow`;
    let addonPlan = ``;
    if (mode === `subscription`) {
      addonPlan = addonPlanId;
    } else if (mode === `follow` && mainPlan) {
      addonPlan = addonPlanId;
    }

    const addonItem = {
      id: addonId,
      quantity: parseInt(addon.getAttribute(`data-addon-qty`)) || 1,
    };
    if (addonPlan) addonItem.selling_plan = addonPlan;

    return [main, addonItem];
  };

  window.NSS_DIRECT_CHECKOUT = async (form, opts = {}) => {
    const root = routeRoot();
    const formData = new FormData(form);
    if (opts.otp) formData.delete(`selling_plan`);
    try {
      await fetch(root + `cart/clear.js`, { method: `POST` });
      const res = await fetch(root + `cart/add.js`, { method: `POST`, body: formData });
      if (!res.ok) throw new Error(`add failed (${res.status})`);
      window.location.href = root + `checkout`;
    } catch (err) {
      console.error(`[NSS][Rebuy Cart] Direct checkout failed:`, err);
      openSmartCart();
    }
  };

  const formSubmitButton = (form) =>
    form.querySelector(`[nss-add-to-cart-btn]`) ||
    form.querySelector(`button[type="submit"]`) ||
    form.querySelector(`[name="add"]`);
  const setButtonLoading = (btn, on) => {
    if (!btn) return;
    btn.classList.toggle(`is-loading`, on);
    if (on) btn.setAttribute(`aria-busy`, `true`);
    else btn.removeAttribute(`aria-busy`);
  };

  const formAddToCart = (form) => {
    if (!form) return;
    if (form.closest(`[data-direct-checkout]`) || form.querySelector(`[data-direct-checkout]`)) {
      window.NSS_DIRECT_CHECKOUT(form);
      return;
    }
    const submitBtn = formSubmitButton(form);
    if (submitBtn && submitBtn.classList.contains(`is-loading`)) return;
    setButtonLoading(submitBtn, true);
    const items = window.NSS_BUILD_ADD_ITEMS(form);
    const request = items
      ? fetch(routeRoot() + `cart/add.js`, {
          method: `POST`,
          headers: { "Content-Type": `application/json` },
          body: JSON.stringify({ items }),
        })
      : fetch(routeRoot() + `cart/add.js`, { method: `POST`, body: new FormData(form) });
    request
      .then((res) => {
        if (!res.ok) throw new Error(`add failed (${res.status})`);
        document.dispatchEvent(new CustomEvent(`nss:cart:added`));
        openSmartCart();
      })
      .catch((err) => {
        document.dispatchEvent(new CustomEvent(`nss:cart:add-error`, { detail: err }));
        console.error(`[NSS][Rebuy Cart] ATC failed:`, err);
      })
      .finally(() => setButtonLoading(submitBtn, false));
  };

  // On the cart page nss-cart-template.js owns ATC (it refreshes the page
  // cart in place), so these global interceptors stand down there.
  document.addEventListener(`submit`, (e) => {
    if (hasThemeCartTemplate()) return;
    if (
      e.target.classList.contains(`shopify-product-form`) ||
      e.target.getAttribute(`action`) === `/cart/add`
    ) {
      e.preventDefault();
      formAddToCart(e.target);
    }
  });

  document.addEventListener(`click`, (e) => {
    if (hasThemeCartTemplate()) return;
    if (e.target.getAttribute(`name`) === `add`) {
      const form = e.target.closest(`form`);
      if (form) {
        e.preventDefault();
        formAddToCart(form);
      }
    }
  });

  // Page mode: the header cart icon (and any /cart link) goes to the cart
  // page. Capture phase so it runs before Rebuy's element-level handler,
  // which would otherwise open the Smart Cart.
  document.addEventListener(
    `click`,
    (e) => {
      if (!isPageMode()) return;
      const link = e.target.closest(`[nss-open-cart-drawer], a[href$="/cart"]`);
      if (!link) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      goToCartPage();
    },
    true
  );

  // Old /cart requests are redirected in theme.liquid (currently to
  // /?viewcart=true, previously /#cart); open the Smart Cart on either marker.
  if (
    window.location.hash === `#cart` ||
    new URLSearchParams(window.location.search).get(`viewcart`) === `true`
  ) {
    openSmartCart();
  }

  // Fallback if Rebuy initialized before this script's listeners registered.
  let attachTries = 0;
  const attachTimer = setInterval(() => {
    attachTries += 1;
    attachAll(null);
    if ((smartCart && findVm() && findVm().__nssBundlesAttached) || attachTries >= 40) {
      clearInterval(attachTimer);
    }
  }, 500);

  // ---- Giftnote app button: restyle inside its open shadow root ----
  // #button lives in the Giftnote element's shadow root, so theme CSS can't
  // reach it. Inject a <style> into that shadow root instead. Text: DM Sans
  // 12/16 mobile -> 14/18 desktop (>=990), underlined; a 16px "+" icon before.
  const GIFTNOTE_PLUS_SVG =
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8.00008 2.66666C8.17689 2.66666 8.34646 2.73689 8.47149 2.86192C8.59651 2.98694 8.66675 3.15651 8.66675 3.33332V7.33332H12.6667C12.8436 7.33332 13.0131 7.40356 13.1382 7.52859C13.2632 7.65361 13.3334 7.82318 13.3334 7.99999C13.3334 8.1768 13.2632 8.34637 13.1382 8.47139C13.0131 8.59642 12.8436 8.66666 12.6667 8.66666H8.66675V12.6667C8.66675 12.8435 8.59651 13.013 8.47149 13.1381C8.34646 13.2631 8.17689 13.3333 8.00008 13.3333C7.82327 13.3333 7.6537 13.2631 7.52868 13.1381C7.40365 13.013 7.33342 12.8435 7.33342 12.6667V8.66666H3.33341C3.1566 8.66666 2.98703 8.59642 2.86201 8.47139C2.73699 8.34637 2.66675 8.1768 2.66675 7.99999C2.66675 7.82318 2.73699 7.65361 2.86201 7.52859C2.98703 7.40356 3.1566 7.33332 3.33341 7.33332H7.33342V3.33332C7.33342 3.15651 7.40365 2.98694 7.52868 2.86192C7.6537 2.73689 7.82327 2.66666 8.00008 2.66666Z" fill="black"/></svg>`;
  const GIFTNOTE_PLUS_URI = `data:image/svg+xml,${encodeURIComponent(GIFTNOTE_PLUS_SVG)}`;
  const GIFTNOTE_CSS = `
    #button {
      color: #000;
      padding: 0;
      height: auto;
      border: none;
      border-radius: 0;
      font-family: "DM Sans", sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.12px;
    }
    #add,
    #edit {
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: 7%;
      text-underline-offset: auto;
      text-underline-position: from-font;
    }
    #button:hover #add,
    #button:hover #edit {
      text-decoration-color: transparent;
    }
    #button::before {
      content: "";
      display: inline-block;
      width: 16px;
      height: 16px;
      aspect-ratio: 1 / 1;
      margin-right: 4px;
      vertical-align: middle;
      background: url("${GIFTNOTE_PLUS_URI}") center / 16px 16px no-repeat;
    }
    @media (min-width: 990px) {
      #button {
        font-size: 14px;
        line-height: 18px;
        letter-spacing: -0.14px;
      }
    }
  `;
  const giftnoteStyled = new WeakSet();
  const ensureGiftnoteStyled = (el, tries) => {
    if (giftnoteStyled.has(el)) return;
    const root = el.shadowRoot;
    const btn = root && root.getElementById(`button`);
    if (!root || !btn) {
      if ((tries || 0) < 40) {
        setTimeout(() => ensureGiftnoteStyled(el, (tries || 0) + 1), 150);
      }
      return;
    }
    if (!root.getElementById(`nss-giftnote-style`)) {
      const style = document.createElement(`style`);
      style.id = `nss-giftnote-style`;
      style.textContent = GIFTNOTE_CSS;
      root.appendChild(style);
    }
    giftnoteStyled.add(el);
  };
  const scanGiftnotes = () =>
    document
      .querySelectorAll(`giftnote-button`)
      .forEach((el) => ensureGiftnoteStyled(el));
  scanGiftnotes();
  if (document.body) {
    new MutationObserver(scanGiftnotes).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
